import dayjs, { Dayjs } from 'dayjs';
import { getDaysDifference, isFirstSameOrBeforeSecond } from '../../utils/dateCalculations';
import { TimetableBlockType } from './graphql';
import type { ProcessedTimetableBlockType } from './useTimetableBlock';

/**
 * Start und Ende eines Abschnitts sind beide inklusive. Ein Abschnitt mit nur einem Tag
 * (Start = Ende) belegt also genau diesen Tag – mit exklusivem Ende galt er als leer, darüber
 * entstand ein zusätzlicher Leerabschnitt und die Nachbargrenzen im Formular wurden unerfüllbar.
 */
const coversDay = (block: TimetableBlockType, day: Dayjs): boolean =>
  isFirstSameOrBeforeSecond(dayjs(block.start), day) && isFirstSameOrBeforeSecond(day, dayjs(block.end));

export const createTimetableBlocks = (
  schoolYearStart: Dayjs,
  schoolYearEnd: Dayjs,
  timetableBlocks: TimetableBlockType[],
): ProcessedTimetableBlockType[] => {
  const emptyBlocks: ProcessedTimetableBlockType[] = [];
  let currentStart = schoolYearStart;

  while (isFirstSameOrBeforeSecond(currentStart, schoolYearEnd)) {
    const overlappingBlock = timetableBlocks.find((block) => coversDay(block, currentStart));

    if (!overlappingBlock) {
      let emptyBlockEnd = currentStart;

      while (
        isFirstSameOrBeforeSecond(emptyBlockEnd, schoolYearEnd) &&
        !timetableBlocks.some((block) => coversDay(block, emptyBlockEnd))
      ) {
        emptyBlockEnd = emptyBlockEnd.add(1, 'day');
      }

      emptyBlocks.push({
        uuid: '',
        name: '',
        start: currentStart.toDate(),
        end: emptyBlockEnd.subtract(1, 'day').toDate(),
        days: getDaysDifference(currentStart, emptyBlockEnd.subtract(1, 'day'), false),
        empty: true,
        currentActive:
          isFirstSameOrBeforeSecond(currentStart.toDate(), new Date()) &&
          isFirstSameOrBeforeSecond(new Date(), emptyBlockEnd.toDate()),
      });

      currentStart = emptyBlockEnd;
    } else {
      currentStart = dayjs(overlappingBlock.end).add(1, 'day');
    }
  }

  const processedTimetableBlocks = timetableBlocks.map((block) => ({
    uuid: block.uuid,
    name: block.name,
    start: new Date(block.start),
    end: new Date(block.end),
    days: getDaysDifference(block.start, block.end, false),
    empty: false,
    currentActive:
      isFirstSameOrBeforeSecond(block.start, new Date()) && isFirstSameOrBeforeSecond(new Date(), block.end),
  }));
  return [...processedTimetableBlocks, ...emptyBlocks.map((eb) => ({ ...eb, uuid: eb.start.toString() }))].sort(
    (a, b) => a.start.getTime() - b.start.getTime(),
  );
};
